import {
  describeDeadline,
  describeRecurrence,
  LAST_MONTH_DAY,
  RECURRENCE_KIND_LABELS,
  RECURRENCE_KINDS,
  type RecurrenceKind,
  TASK_PRIORITIES,
  TASK_PRIORITY_LABELS,
  type TaskPriority,
  type TaskTemplateItem,
  WEEKDAY_SHORT,
} from '@erp/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import clsx from 'clsx';
import { ChevronLeft, Pause, Play, Plus, Repeat, Trash2 } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { Dialog } from '@/components/dialog';
import {
  Avatar,
  Badge,
  Button,
  ErrorBox,
  Field,
  Input,
  PageHeader,
  Select,
  Spinner,
  Textarea,
} from '@/components/ui';
import { UserPicker } from '@/components/user-picker';
import { ApiError, api } from '@/lib/api';
import { useCan, useUser } from '@/lib/auth';
import { formatMoment } from '@/lib/format';
import { projectsQuery } from '@/lib/queries';

const templatesQuery = {
  queryKey: ['tasks', 'templates'],
  queryFn: () => api<TaskTemplateItem[]>('/task-templates'),
};

/** Повторяющиеся задачи: шаблоны, которые сами ставят задачу по расписанию. */
export function RecurringTasksPage() {
  const can = useCan();
  const list = useQuery(templatesQuery);
  const [editing, setEditing] = useState<TaskTemplateItem | 'new' | null>(null);

  return (
    <div className="max-w-[1100px]">
      <Link to="/tasks" className="mb-4 inline-flex items-center gap-1 text-[13px] text-steel hover:text-ink">
        <ChevronLeft className="size-4" aria-hidden />
        Все задачи
      </Link>
      <PageHeader
        title="Повторяющиеся задачи"
        description="Задача ставится сама по расписанию: каждый день, по дням недели или раз в месяц — от вашего имени, со сроком и чек-листом."
        actions={
          can('tasks.task.create') && (
            <Button onClick={() => setEditing('new')}>
              <Plus className="size-4" aria-hidden />
              Новая повторяющаяся
            </Button>
          )
        }
      />
      {editing && (
        <TemplateDialog template={editing === 'new' ? undefined : editing} onClose={() => setEditing(null)} />
      )}
      {list.isPending ? (
        <Spinner />
      ) : list.error ? (
        <ErrorBox error={list.error} />
      ) : list.data.length === 0 ? (
        <div className="panel px-6 py-10 text-center">
          <Repeat className="mx-auto size-6 text-steel" aria-hidden />
          <p className="mt-2 font-medium">Повторяющихся задач пока нет</p>
          <p className="mt-1 text-[13.5px] text-steel">
            Например: «Отчёт по отгрузкам» каждую пятницу в 09:00 со сроком до 18:00.
          </p>
        </div>
      ) : (
        <ul className="panel divide-y divide-line">
          {list.data.map((tpl) => (
            <TemplateRow key={tpl.id} tpl={tpl} onEdit={() => setEditing(tpl)} />
          ))}
        </ul>
      )}
    </div>
  );
}

function TemplateRow({ tpl, onEdit }: { tpl: TaskTemplateItem; onEdit: () => void }) {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: ['tasks'] });
  const [message, setMessage] = useState<string | null>(null);
  const toggle = useMutation({
    mutationFn: () =>
      api(`/task-templates/${tpl.id}/active`, { method: 'POST', body: { isActive: !tpl.isActive } }),
    onSuccess: refresh,
  });
  const run = useMutation({
    mutationFn: () =>
      api<{ id: string; number: number }>(`/task-templates/${tpl.id}/run`, { method: 'POST' }),
    onSuccess: (r) => {
      setMessage(`Поставлена задача #${r.number}`);
      refresh();
    },
  });
  const remove = useMutation({
    mutationFn: () => api(`/task-templates/${tpl.id}`, { method: 'DELETE' }),
    onSuccess: refresh,
  });
  const error = toggle.error ?? run.error ?? remove.error;

  return (
    <li
      className={clsx(
        'flex flex-wrap items-start gap-x-4 gap-y-2 px-4 py-3',
        !tpl.isActive && 'bg-sunken/40',
      )}
    >
      <div className="min-w-0 flex-1 basis-[280px]">
        <div className="flex flex-wrap items-center gap-2">
          {tpl.canEdit ? (
            <button type="button" onClick={onEdit} className="text-left font-medium hover:text-link">
              {tpl.title}
            </button>
          ) : (
            <span className="font-medium">{tpl.title}</span>
          )}
          {!tpl.isActive && <Badge>на паузе</Badge>}
          {tpl.priority === 'high' && <Badge tone="danger">важная</Badge>}
        </div>
        <p className="mt-0.5 text-[13px] text-ink-soft">
          {describeRecurrence(tpl.rule)}, {describeDeadline(tpl.deadlineDays, tpl.deadlineTime)}
          {tpl.project && ` · ${tpl.project.name}`}
        </p>
        <p className="mt-0.5 text-[12.5px] text-steel">
          {tpl.isActive && tpl.nextRunAt ? `Следующая: ${formatMoment(tpl.nextRunAt)}` : 'Не ставится'}
          {tpl.createdCount > 0 && (
            <>
              {' · поставлено '}
              {tpl.createdCount}
              {tpl.lastTask && (
                <>
                  {', последняя '}
                  <Link to="/tasks/$id" params={{ id: tpl.lastTask.id }} className="link">
                    #{tpl.lastTask.number}
                  </Link>
                </>
              )}
            </>
          )}
        </p>
        {tpl.lastError && <p className="mt-1 text-[12.5px] text-danger">{tpl.lastError}</p>}
        {message && <p className="mt-1 text-[12.5px] text-ok">{message}</p>}
        {error && <p className="mt-1 text-[12.5px] text-danger">{error.message}</p>}
      </div>
      <div className="flex items-center gap-2 text-[13px]">
        <Avatar name={tpl.assignee.fullName} userId={tpl.assignee.userId} size={24} />
        <span className="max-w-[180px] truncate">{tpl.assignee.fullName}</span>
      </div>
      {tpl.canEdit && (
        <div className="flex items-center gap-1">
          <Button size="sm" variant="secondary" loading={run.isPending} onClick={() => run.mutate()}>
            Поставить сейчас
          </Button>
          <Button
            size="sm"
            variant="ghost"
            aria-label={tpl.isActive ? 'Поставить на паузу' : 'Возобновить'}
            title={tpl.isActive ? 'Поставить на паузу' : 'Возобновить'}
            loading={toggle.isPending}
            onClick={() => toggle.mutate()}
          >
            {tpl.isActive ? <Pause className="size-4" /> : <Play className="size-4" />}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            aria-label="Удалить"
            title="Удалить (поставленные задачи останутся)"
            loading={remove.isPending}
            onClick={() => {
              if (window.confirm(`Удалить «${tpl.title}»? Уже поставленные задачи останутся.`))
                remove.mutate();
            }}
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      )}
    </li>
  );
}

function TemplateDialog({ template, onClose }: { template?: TaskTemplateItem; onClose: () => void }) {
  const me = useUser();
  const qc = useQueryClient();
  const projects = useQuery(projectsQuery);
  const [assignee, setAssignee] = useState<string[]>([template?.assignee.userId ?? me.id]);
  const [coexecutors, setCoexecutors] = useState<string[]>(template?.coexecutorIds ?? []);
  const [observers, setObservers] = useState<string[]>(template?.observerIds ?? []);
  const [kind, setKind] = useState<RecurrenceKind>(template?.rule.kind ?? 'weekly');
  const [weekdays, setWeekdays] = useState<number[]>(
    template?.rule.weekdays.length ? template.rule.weekdays : [1],
  );
  const [deadlineMode, setDeadlineMode] = useState<'none' | 'days'>(
    template && template.deadlineDays === null ? 'none' : 'days',
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      template
        ? api(`/task-templates/${template.id}`, { method: 'PUT', body })
        : api('/task-templates', { method: 'POST', body }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['tasks', 'templates'] });
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setErrors(err.fields);
        setFormError(Object.keys(err.fields).length ? null : err.message);
      }
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    save.mutate({
      title: String(fd.get('title') ?? ''),
      description: String(fd.get('description') ?? ''),
      assigneeId: assignee[0],
      coexecutorIds: coexecutors,
      observerIds: observers,
      projectId: (fd.get('projectId') as string) || null,
      priority: fd.get('priority') as TaskPriority,
      requireReview: fd.get('requireReview') === 'on',
      checklist: String(fd.get('checklist') ?? '')
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean),
      kind,
      weekdays: kind === 'weekly' ? weekdays : [],
      monthDay: kind === 'monthly' ? Number(fd.get('monthDay')) : null,
      time: String(fd.get('time') ?? ''),
      deadlineDays: deadlineMode === 'none' ? null : Number(fd.get('deadlineDays') ?? 0),
      deadlineTime: String(fd.get('deadlineTime') || '18:00'),
      isActive: template?.isActive ?? true,
    });
  }

  return (
    <Dialog
      title={template ? 'Повторяющаяся задача' : 'Новая повторяющаяся задача'}
      onClose={onClose}
      width={680}
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <Field label="Название" error={errors.title}>
          {(p) => <Input {...p} name="title" defaultValue={template?.title} maxLength={300} required />}
        </Field>
        <Field label="Описание" error={errors.description}>
          {(p) => <Textarea {...p} name="description" defaultValue={template?.description} rows={3} />}
        </Field>

        <fieldset className="min-w-0 rounded-[var(--radius-control)] border border-line p-3">
          <legend className="px-1 text-[13px] font-medium">Расписание</legend>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label htmlFor="tpl-kind" className="field-label">
                Как часто
              </label>
              <Select id="tpl-kind" value={kind} onChange={(e) => setKind(e.target.value as RecurrenceKind)}>
                {RECURRENCE_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {RECURRENCE_KIND_LABELS[k]}
                  </option>
                ))}
              </Select>
            </div>
            {kind === 'monthly' && (
              <div>
                <label htmlFor="tpl-day" className="field-label">
                  Число
                </label>
                <Select id="tpl-day" name="monthDay" defaultValue={String(template?.rule.monthDay ?? 1)}>
                  {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                    <option key={d} value={d}>
                      {d}-е
                    </option>
                  ))}
                  <option value={LAST_MONTH_DAY}>последний день</option>
                </Select>
              </div>
            )}
            <div>
              <label htmlFor="tpl-time" className="field-label">
                Ставить в
              </label>
              <Input
                id="tpl-time"
                type="time"
                name="time"
                defaultValue={template?.rule.time ?? '09:00'}
                className="w-[120px]"
                aria-invalid={Boolean(errors.time)}
              />
            </div>
          </div>
          {kind === 'weekly' && (
            <fieldset className="mt-3 min-w-0">
              <legend className="field-label">Дни недели</legend>
              <div className="flex flex-wrap gap-1.5">
                {WEEKDAY_SHORT.map((label, i) => {
                  const day = i + 1;
                  const on = weekdays.includes(day);
                  return (
                    <button
                      key={label}
                      type="button"
                      aria-pressed={on}
                      onClick={() =>
                        setWeekdays((prev) => (on ? prev.filter((d) => d !== day) : [...prev, day].sort()))
                      }
                      className={clsx(
                        'h-8 w-10 rounded border text-[13px]',
                        on
                          ? 'border-graphite bg-graphite text-white'
                          : 'border-line-strong bg-surface hover:bg-sunken',
                      )}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              {errors.weekdays && <p className="mt-1 text-[12.5px] text-danger">{errors.weekdays}</p>}
            </fieldset>
          )}
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <div>
              <label htmlFor="tpl-deadline" className="field-label">
                Срок задачи
              </label>
              <Select
                id="tpl-deadline"
                value={deadlineMode}
                onChange={(e) => setDeadlineMode(e.target.value as 'none' | 'days')}
              >
                <option value="days">Через … дней</option>
                <option value="none">Без срока</option>
              </Select>
            </div>
            {deadlineMode === 'days' && (
              <>
                <div>
                  <label htmlFor="tpl-days" className="field-label">
                    Дней (0 — в тот же день)
                  </label>
                  <Input
                    id="tpl-days"
                    type="number"
                    name="deadlineDays"
                    min={0}
                    max={90}
                    defaultValue={template?.deadlineDays ?? 0}
                    className="w-[120px]"
                  />
                </div>
                <div>
                  <label htmlFor="tpl-dtime" className="field-label">
                    До
                  </label>
                  <Input
                    id="tpl-dtime"
                    type="time"
                    name="deadlineTime"
                    defaultValue={template?.deadlineTime ?? '18:00'}
                    className="w-[120px]"
                  />
                </div>
              </>
            )}
          </div>
        </fieldset>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Исполнитель" error={errors.assigneeId}>
            {(p) => (
              <UserPicker id={p.id} value={assignee} onChange={setAssignee} invalid={p['aria-invalid']} />
            )}
          </Field>
          <Field label="Проект" error={errors.projectId}>
            {(p) => (
              <Select {...p} name="projectId" defaultValue={template?.project?.id ?? ''}>
                <option value="">Без проекта</option>
                {projects.data
                  ?.filter((pr) => !pr.isArchived)
                  .map((pr) => (
                    <option key={pr.id} value={pr.id}>
                      {pr.name}
                    </option>
                  ))}
              </Select>
            )}
          </Field>
          <Field label="Соисполнители">
            {(p) => <UserPicker id={p.id} value={coexecutors} onChange={setCoexecutors} multiple />}
          </Field>
          <Field label="Наблюдатели">
            {(p) => <UserPicker id={p.id} value={observers} onChange={setObservers} multiple />}
          </Field>
          <Field label="Приоритет">
            {(p) => (
              <Select {...p} name="priority" defaultValue={template?.priority ?? 'normal'}>
                {TASK_PRIORITIES.map((pr) => (
                  <option key={pr} value={pr}>
                    {TASK_PRIORITY_LABELS[pr]}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <label className="mt-6 inline-flex items-center gap-2 text-[13.5px]">
            <input
              type="checkbox"
              name="requireReview"
              defaultChecked={template?.requireReview}
              className="size-4 accent-[var(--color-graphite)]"
            />
            Проверить после выполнения
          </label>
        </div>
        <Field label="Чек-лист — пункт на строку">
          {(p) => <Textarea {...p} name="checklist" defaultValue={template?.checklist.join('\n')} rows={3} />}
        </Field>

        {formError && <p className="text-[13px] text-danger">{formError}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" loading={save.isPending}>
            {template ? 'Сохранить' : 'Создать'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
